import React, { useState, useEffect } from 'react';
import { ModalWrapper } from './ModalWrapper';

interface DeclineDocumentVerificationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (reason: string, message: string) => Promise<void>;
    isSaving?: boolean;
}

export const DeclineDocumentVerificationModal: React.FC<DeclineDocumentVerificationModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    isSaving = false
}) => {
    const [selectedReason, setSelectedReason] = useState('');
    const [messageText, setMessageText] = useState('');
    const [errors, setErrors] = useState<{ reason?: string; message?: string }>({});
    const [submitError, setSubmitError] = useState<string | null>(null);

    const declineReasons = [
        'אינו מאשר אפוטרופסות בלעדית',
        'אינו מבית משפט אזרחי',
        'מסמך חסר או לא קריא',
        'אינו תואם לפרטי ההורה',
        'מסמך שגוי (לדוגמה: מסמך מזונות)',
        'אחר'
    ];

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedReason('');
            setMessageText('');
            setErrors({});
            setSubmitError(null);
        }
    }, [isOpen]);

    const validate = () => {
        const newErrors: { reason?: string; message?: string } = {};

        if (!selectedReason) {
            newErrors.reason = 'Please select a decline reason';
        }

        if (!messageText.trim()) {
            newErrors.message = 'Please enter a message to the patient';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setSubmitError(null); // Clear any previous errors

        try {
            await onSubmit(selectedReason, messageText);
            // Note: Parent handler (handleDeclineSubmit) closes both modals
        } catch (error: any) {
            console.error('[DeclineModal] Failed to submit:', error);
            setSubmitError(error?.message || 'Failed to decline verification. Please try again.');
            // Keep modal open on error so user can see the issue
        }
    };

    const handleCancel = () => {
        setSelectedReason('');
        setMessageText('');
        setErrors({});
        onClose();
    };

    return (
        <ModalWrapper modalId="DeclineDocumentVerificationModal"
            isOpen={isOpen}
            onClose={handleCancel}
            title="Decline Document Verification"
            width="max-w-lg"
        >
            <form onSubmit={handleSubmit}>
                {/* Decline Reason Dropdown */}
                <div className="mb-5">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Decline reason <span className="text-red-500">*</span>
                    </label>
                    <select
                        value={selectedReason}
                        onChange={(e) => {
                            setSelectedReason(e.target.value);
                            setErrors(prev => ({ ...prev, reason: undefined }));
                            setSubmitError(null);
                        }}
                        className={`w-full px-4 py-2 rounded-lg border ${
                            errors.reason ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
                        } focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent`}
                        disabled={isSaving}
                    >
                        <option value="">Select a reason...</option>
                        {declineReasons.map((reason, index) => (
                            <option key={index} value={reason}>
                                {reason}
                            </option>
                        ))}
                    </select>
                    {errors.reason && (
                        <p className="text-red-600 text-xs mt-1">{errors.reason}</p>
                    )}
                </div>

                {/* Message to Patient Textarea */}
                <div className="mb-5">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Message to patient <span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                        Used as the content of the patient notification email.
                    </p>
                    <textarea
                        value={messageText}
                        onChange={(e) => {
                            setMessageText(e.target.value);
                            setErrors(prev => ({ ...prev, message: undefined }));
                            setSubmitError(null);
                        }}
                        placeholder="Enter your message to the patient..."
                        rows={6}
                        className={`w-full px-4 py-2 rounded-lg border ${
                            errors.message ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
                        } focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent resize-none`}
                        disabled={isSaving}
                    />
                    {errors.message && (
                        <p className="text-red-600 text-xs mt-1">{errors.message}</p>
                    )}
                </div>

                {/* Submit Error Message */}
                {submitError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                        {submitError}
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 justify-end mt-6">
                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={isSaving}
                        className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={isSaving || !selectedReason || !messageText.trim()}
                        className="px-6 py-2 text-sm font-medium text-white bg-[#3A6B75] rounded-lg hover:bg-[#2D5A60] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3A6B75] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSaving ? 'Sending...' : 'Send'}
                    </button>
                </div>
            </form>
        </ModalWrapper>
    );
};
